import React from 'react';
import { Helmet } from 'react-helmet';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface StructuredDataProps {
  type: 'Organization' | 'Product' | 'Brand' | 'Service' | 'BreadcrumbList';
  data: any;
}

export const StructuredData: React.FC<StructuredDataProps> = ({ type, data }) => {
  const generateStructuredData = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://moafriends.com';
    
    switch (type) {
      case 'Organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '모아프렌즈',
          url: baseUrl,
          logo: `${baseUrl}/logo.png`,
          sameAs: [
            'https://www.facebook.com/moafriends',
            'https://www.instagram.com/moafriends',
            'https://www.youtube.com/moafriends'
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+82-2-1234-5678',
            contactType: 'customer service',
            areaServed: 'KR',
            availableLanguage: ['Korean', 'English']
          }
        };
        
      case 'Product':
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: data.name,
          description: data.description,
          image: data.image,
          brand: {
            '@type': 'Brand',
            name: data.brandName
          },
          offers: {
            '@type': 'Offer',
            price: data.price,
            priceCurrency: 'KRW',
            availability: data.availability || 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: data.brandName
            }
          },
          aggregateRating: data.rating ? {
            '@type': 'AggregateRating',
            ratingValue: data.rating.value,
            reviewCount: data.rating.count
          } : undefined
        };
        
      case 'Brand':
        return {
          '@context': 'https://schema.org',
          '@type': 'Brand',
          name: data.name,
          description: data.description,
          logo: data.logo,
          image: data.image,
          url: data.url,
          slogan: data.slogan,
          aggregateRating: data.rating ? {
            '@type': 'AggregateRating',
            ratingValue: data.rating.value,
            reviewCount: data.rating.count
          } : undefined
        };
        
      case 'Service':
        return {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: data.name,
          description: data.description,
          provider: {
            '@type': 'Organization',
            name: data.providerName || '모아프렌즈'
          },
          serviceType: data.serviceType || '멤버십 서비스',
          areaServed: {
            '@type': 'Country',
            name: 'South Korea'
          },
          offers: data.offers ? {
            '@type': 'Offer',
            price: data.offers.price,
            priceCurrency: 'KRW',
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: data.offers.price,
              priceCurrency: 'KRW',
              unitCode: data.offers.unitCode || 'MON'
            }
          } : undefined
        };
        
      case 'BreadcrumbList':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: data.items.map((item: BreadcrumbItem, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${baseUrl}${item.url}`
          }))
        };
        
      default:
        return null;
    }
  };
  
  const structuredData = generateStructuredData();
  
  if (!structuredData) return null;
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};